import {
  type CreatePaymentMethodResponse,
  type CreatePaymentMethodErrorCode,
} from '../../types/stripe/createPaymentMethod';
import { TokenizationErrorCodes } from '../../types/TokenizationErrorCodes';
import { captureException } from '../../utils/errorReporter';

/**
 * Stripe codes
 * https://stripe.com/docs/error-codes - error.code
 * https://stripe.com/docs/declines/codes - error.decline_code (in case error.code === 'card_decline')
 */

const reportUnexpectedStripeError = (error: CreatePaymentMethodResponse['error']) =>
  captureException(
    new Error(
      `Unexpected Stripe error. type=${error?.type} code=${error?.code}, decline_code=${error?.decline_code}, message=${error?.message} `,
    ),
    false,
  );

export const mapStripeTokenizeErrorToErrorCode = (error: CreatePaymentMethodResponse['error']): number => {
  if (error) {
    switch (error.code as CreatePaymentMethodErrorCode) {
      case 'invalid_number':
      case 'incorrect_number':
        return TokenizationErrorCodes.InvalidCardNumber;
      case 'invalid_expiry_month':
      case 'invalid_expiry_year':
      case 'expired_card':
        return TokenizationErrorCodes.InvalidExpirationDate;
      case 'incorrect_cvc':
      case 'invalid_cvc':
        return TokenizationErrorCodes.InvalidCVV;
      case 'postal_code_invalid':
      case 'incorrect_zip':
        return TokenizationErrorCodes.InvalidZipCode;
      case 'invalid_card_type':
        return TokenizationErrorCodes.CardTypeIsNotSupported;
      case 'invalid_characters':
        return TokenizationErrorCodes.GeneralValidationError;
      case 'card_declined':
        switch (error.decline_code) {
          case 'incorrect_number':
          case 'invalid_number':
            return TokenizationErrorCodes.InvalidCardNumber;
          case 'incorrect_cvc':
          case 'invalid_cvc':
            return TokenizationErrorCodes.InvalidCVV;
          case 'expired_card':
          case 'invalid_expiry_month':
          case 'invalid_expiry_year':
            return TokenizationErrorCodes.InvalidExpirationDate;
          case 'incorrect_zip':
            return TokenizationErrorCodes.InvalidZipCode;
          case 'authentication_required':
            return TokenizationErrorCodes.ThreeDSecureFailed;
          case 'card_not_supported':
            return TokenizationErrorCodes.CardTypeIsNotSupported;
          default: {
            reportUnexpectedStripeError(error);
            return TokenizationErrorCodes.GeneralTokenizationError;
          }
        }
      default:
    }
  }
  reportUnexpectedStripeError(error);
  return TokenizationErrorCodes.GeneralTokenizationError;
};
